/**
 * inbox/v2/shared/constants.js — single source of truth for inbox constants
 * shared between the PWA frontend (inbox/v2/**) and the Netlify Functions
 * backend (netlify/functions/**).
 *
 * #119: Backend imports this file via a relative path shim at
 * `netlify/functions/config/inbox-constants.js` which does nothing but
 * re-export from here. esbuild (Netlify's default bundler) follows the
 * relative import across directories and inlines these constants into
 * each function bundle at deploy time. No magic strings drift between
 * the client and the server.
 */

// broadcastTopic() removed in #81 — the topic is now HMAC-suffixed and
// only derivable on the server. realtime.js receives the full topic string
// from the authenticated /realtime-config endpoint and uses it verbatim.

/** Event names broadcast on the inbox channel. */
export const BROADCAST_EVENTS = Object.freeze({
  MESSAGE_INSERT:    'message_insert',
  MESSAGE_UPDATE:    'message_update',
  EVENT_INSERT:      'event_insert',
  // T1 — cron-scheduled-send emits this on every terminal transition
  // (pending → sent / failed / cancelled-by-cron). The payload carries
  // { id, chat_id, channel_id, status, error_reason } so the open PWA
  // thread can flip the ghost bubble to its Failed / Sending-now state
  // without waiting for the next loadScheduled (or the S23 outbound-
  // message correlation path, which only fires on success).
  SCHEDULED_UPDATE:  'scheduled_update',
});

/** Message status values (mirrors inbox_messages.status column). */
export const MESSAGE_STATUS = Object.freeze({
  PENDING:   'pending',
  SENT:      'sent',
  DELIVERED: 'delivered',
  READ:      'read',
  FAILED:    'failed',
});

/**
 * Status rank — higher wins. Used by the webhook's status correlation logic
 * to refuse to "downgrade" a message (e.g., never set status='sent' on a row
 * that's already 'delivered').
 */
export const STATUS_RANK = Object.freeze({
  [MESSAGE_STATUS.PENDING]:   0,
  [MESSAGE_STATUS.SENT]:      1,
  [MESSAGE_STATUS.DELIVERED]: 2,
  [MESSAGE_STATUS.READ]:      3,
  [MESSAGE_STATUS.FAILED]:    99,
});

/** Direction values (mirrors inbox_messages.direction column). */
export const DIRECTION = Object.freeze({
  INBOUND:  'inbound',
  OUTBOUND: 'outbound',
  SYSTEM:   'system',
});

/** Screen names for $screen store atom. Frontend-only but safe to expose. */
export const SCREENS = Object.freeze({
  INIT:             'init',
  LOGIN:            'login',
  CHAT_LIST:        'chat-list',
  THREAD:           'thread',
  PROFILE:          'profile',
  SETTINGS:         'settings',
  QUICK_REPLIES:    'quick-replies',
  PERMISSION_ERROR: 'permission-error',
});

/**
 * PWA build version. Format: MAJOR.MINOR.PATCH
 *   MAJOR = architecture milestones (Vite migration, framework change)
 *   MINOR = feature batches (FCM push, media archival, realtime stability)
 *   PATCH = bug fixes within a batch
 *
 * Bump in lockstep with CACHE_NAME in inbox/sw.js.
 * History: v1–v125 were iteration counters during rapid development.
 * Switched to semver at 2.0.0 (2026-04-17) for production readiness.
 */
export const BUILD_VERSION = '2.1.43';

/**
 * Internal event types broadcast by the webhook after persisting rows to
 * inbox_events.
 */
export const EVENT_TYPES = Object.freeze({
  MESSAGE_INCOMING:        'message.incoming',
  MESSAGE_OUTGOING:        'message.outgoing',
  MESSAGE_REACTION:        'message.reaction',
  MESSAGE_DELETED:         'message.deleted',
  MESSAGE_STATUS_PREFIX:   'message.status.',
  CHAT_ASSIGNED:           'chat.assigned',
  CHAT_CLOSED:             'chat.closed',
  CHAT_NEW:                'chat.new',
  TEMPLATE_STATUS:         'template.status',
  SYSTEM_EVENT:            'system.event',
});

// ── Session activity window ─────────────────────────────────────────────────
//
// Task #113: the proxy and the webhook must agree on what "actively open"
// means for an inbox session. Two constants define that contract:
//
//   - LAST_ACTIVE_UPDATE_THROTTLE_MS: inbox-proxy debounces PATCHes to
//     inbox_sessions.last_active so a chatty user doesn't thrash the DB.
//     The proxy updates at most once per this window.
//
//   - ACTIVE_SESSION_WINDOW_MS: inbox-webhook.fcmFanOut skips FCM pushes
//     to users whose session updated last_active within this window,
//     because the open tab already shows the event via Supabase Realtime
//     broadcast and the push would be redundant.
//
// CRITICAL: ACTIVE_SESSION_WINDOW_MS MUST be strictly greater than
// LAST_ACTIVE_UPDATE_THROTTLE_MS. Otherwise, a user who used the inbox
// at t=0 and then sits idle until t = throttle + 1s will appear INACTIVE
// to the webhook and get a duplicate push, even though their tab is
// still wired up to realtime.
export const LAST_ACTIVE_UPDATE_THROTTLE_MS = 60 * 1000;       // 1 minute
export const ACTIVE_SESSION_WINDOW_MS       = 2 * 60 * 1000;   // 2 minutes (must exceed throttle)
